import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { participantesApi } from '../../lib/api';
import logger from '../../lib/logger';

const FIELDS = [
  { key: 'nome_responsavel', label: 'Nome do responsável (1º cônjuge)' },
  { key: 'nome_conjuge', label: 'Nome do cônjuge (2º cônjuge)' },
  { key: 'email', label: 'E-mail', type: 'email' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'cpf', label: 'CPF' },
];

export const EditParticipanteDialog = ({ participante, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (participante) {
      setForm(Object.fromEntries(FIELDS.map(f => [f.key, participante[f.key] || ''])));
    }
  }, [participante, open]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!participante) return;
    setSaving(true);
    try {
      const id = participante.participante_id || participante.id;
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => v.trim() !== '' && v !== (participante[k] || ''))
      );
      if (Object.keys(payload).length === 0) {
        toast.info('Nenhuma alteração para salvar');
        return;
      }
      const res = await participantesApi.update(id, payload);
      toast.success('Dados do participante atualizados');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (error) {
      logger.error('Error updating participante:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao atualizar participante');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-lg" data-testid="edit-participante-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Editar Participante</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Corrija os dados do casal. Somente os campos alterados serão salvos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-gray-400 text-sm mb-1 block">{f.label}</label>
              <Input
                type={f.type || 'text'}
                value={form[f.key] || ''}
                onChange={(e) => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="bg-obsidian-50 border-white/10 text-white"
                data-testid={`edit-${f.key}-input`}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400" data-testid="edit-participante-cancel">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-kairos-orange hover:bg-kairos-orange-dark text-white" data-testid="edit-participante-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
